<script setup>
import {ref, onBeforeMount} from "vue";
import axios from "axios";

// Reactive data stores
const requestCloseReasonsList = ref([]);
const newReasonName = ref("");
const statusText = ref("");
const status = ref(true);
const isLoading = ref(false);

// Fetch all reasons
const fetchRequestCloseReasons = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get("request_close_reasons/");
    requestCloseReasonsList.value = response.data.map((reason) => ({
      ...reason,
      isEditing: false, // Add isEditing flag to each item
    }));
  } catch (error) {
    console.error("Error fetching request close reasons:", error);
    statusText.value = "Ошибка при получении данных";
    status.value = false;
  } finally {
    isLoading.value = false;
  }
};

// Create a new reason
const createRequestCloseReason = async () => {
  if (!newReasonName.value.trim()) {
    statusText.value = "Пожалуйста, введите текст для заявки.";
    status.value = false;
    return;
  }

  try {
    const response = await axios.post("request_close_reasons/", {
      name: newReasonName.value.trim(),
    });
    requestCloseReasonsList.value.push({...response.data, isEditing: false});
    newReasonName.value = ""; // Clear input
    statusText.value = "Текст успешно создан!";
    status.value = true;
  } catch (error) {
    console.error("Error creating a new reason:", error);
    statusText.value = "Ошибка при создании текста!";
    status.value = false;
  }
};

// Enable edit mode for an item
const editReason = (item) => {
  item.isEditing = !item.isEditing;
};

// Save the edited reason
const saveReasonEdit = async (item) => {
  if (!item.name.trim()) {
    statusText.value = "Пожалуйста, введите текст для заявки.";
    status.value = false;
    return;
  }

  try {
    await axios.patch(`request_close_reasons/`, {
      reason_id: item.id,
      name: item.name.trim(),
    });
    item.isEditing = false; // Exit edit mode
    statusText.value = "Текст успешно обновлен!";
    status.value = true;
  } catch (error) {
    console.error("Error updating the reason:", error);
    statusText.value = "Ошибка при обновлении текста!";
    status.value = false;
  }
};

// Change status of a reason
const changeReasonStatus = async (reason_id) => {
  try {
    await axios.patch("request_close_reasons/", {reason_id});
    statusText.value = "Статус успешно обновлен!";
    status.value = true;
    await fetchRequestCloseReasons(); // Refresh the list
  } catch (error) {
    console.error("Error changing status:", error);
    statusText.value = "Ошибка при изменении статуса!";
    status.value = false;
  }
};

// Fetch data before the component is mounted
onBeforeMount(() => {
  fetchRequestCloseReasons();
});
</script>

<template>
  <div class="content-wrapper">
    <div class="container-fluid d-flex justify-content-center align-items-center" style="min-height: 100vh;">
      <!-- Centralized Card -->
      <div class="w-100" style="max-width: 600px;">
        <!-- Section: Display List -->
        <div class="gap-lg-3">
          <h2 class="text-center mb-4">Тексты для заявок</h2>

          <!-- Status Message -->
          <div v-if="statusText" :class="['alert', status ? 'alert-success' : 'alert-danger']">
            {{ statusText }}
          </div>

          <!-- Section: Create New Reason -->
          <div class="mb-4 p-4 bg-white rounded shadow-sm">
            <div class="input-group">
              <input
                  v-model="newReasonName"
                  type="text"
                  class="form-control"
                  placeholder="Новый текст для заявки"
                  @keyup.enter="createRequestCloseReason"
              />
              <button class="btn btn-primary" @click="createRequestCloseReason">
                Создать
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <!-- Render each reason as a card-like item -->
          <div
              v-for="item in requestCloseReasonsList"
              :key="item.id"
              class="bg-light border rounded px-3 py-2 shadow-sm mb-3 d-flex justify-content-between align-items-center"
          >
            <div class="flex-grow-1">
              <template v-if="item.isEditing">
                <input
                    type="text"
                    v-model="item.name"
                    class="form-control"
                    @keyup.enter="saveReasonEdit(item)"
                />
              </template>
              <template v-else>
                {{ item.name }}
              </template>
            </div>
            <div class="d-flex gap-2">
              <!-- Toggle Status Button -->
              <button
                  v-if="!item.isEditing"
                  type="button"
                  :class="['btn btn-sm', item.enabled === 1 ? 'btn-success' : 'btn-danger']"
                  @click="changeReasonStatus(item.id)"
              >
                <i :class="item.enabled === 1 ? 'bi bi-check2' : 'bi bi-x'"></i>
              </button>

              <!-- Edit Button -->
              <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="editReason(item)"
              >
                <i class="bi bi-pen"></i>
              </button>

              <!-- Save Button (Visible in Edit Mode) -->
              <button
                  v-if="item.isEditing"
                  type="button"
                  class="btn btn-sm btn-success"
                  @click="saveReasonEdit(item)"
              >
                <i class="bi bi-check"></i>
              </button>
            </div>
          </div>

          <!-- Fallback message if no reasons are available -->
          <div v-if="!isLoading && requestCloseReasonsList.length === 0" class="text-muted text-center">
            Нет доступных текстов для заявок.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.content-wrapper {
  background-color: #f8f9fa; /* Light background color */
  padding: 20px;
}

.bg-white {
  background-color: #ffffff;
}

.input-group {
  display: flex;
  gap: 10px;
}

.btn-primary {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004080;
}

.text-muted {
  font-style: italic;
}

.alert {
  margin-bottom: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.25rem;
}
</style>